import { Link, useLocation, useNavigate } from "react-router-dom";
import { HeartHandshake, LogOut, Crown, ArrowLeftRight, User, Moon, Sun, Timer, Lock } from "lucide-react";
import { useAuth } from "../context/AuthContext";

function fmtLeft(sec) {
  const m = Math.max(0, Math.ceil(sec / 60));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
}

export default function Header() {
  const { user, logout, isPremium, pro, lockCarer, evening, toggleEvening } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isUserMode = location.pathname.startsWith("/use");

  const toggleMode = () => {
    if (isUserMode) {
      navigate("/app");
    } else {
      lockCarer();
      navigate("/use");
    }
  };

  const handleLogout = async () => {
    navigate("/", { replace: true });
    await logout();
  };

  return (
    <header data-testid="header" className="sticky top-0 z-40 bg-background border-b-2 border-border">
      <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
        <Link to={isUserMode ? "/use" : "/app"} data-testid="logo-link" className="flex items-center gap-2 min-h-[48px]">
          <span className="w-10 h-10 rounded-2xl bg-primary grid place-items-center">
            <HeartHandshake size={24} strokeWidth={2.5} className="text-white" />
          </span>
          <span className="text-xl font-extrabold tracking-tight">RoutineBuddy</span>
        </Link>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <button
            data-testid="evening-mode-toggle"
            onClick={toggleEvening}
            aria-label={evening ? "Switch to daytime brightness" : "Switch to evening dim mode"}
            title={evening ? "Daytime mode" : "Evening mode"}
            className="min-h-[48px] min-w-[48px] rounded-full border-2 border-foreground/25 bg-white grid place-items-center transition-colors duration-200 hover:bg-secondary"
          >
            {evening ? <Sun size={20} strokeWidth={2.5} /> : <Moon size={20} strokeWidth={2.5} />}
          </button>
          <button
            data-testid="mode-toggle-button"
            onClick={toggleMode}
            className="min-h-[48px] px-4 rounded-full border-2 border-foreground/25 bg-white font-bold text-sm flex items-center gap-2 transition-colors duration-200 hover:bg-secondary"
          >
            {isUserMode ? <User size={20} strokeWidth={2.5} /> : <ArrowLeftRight size={20} strokeWidth={2.5} />}
            <span className="hidden min-[420px]:inline">{isUserMode ? "Carer Mode" : "User Mode"}</span>
          </button>
          {!isUserMode && (
            isPremium ? (
              <span data-testid="premium-badge" className="min-h-[48px] px-4 rounded-full bg-[#ECC94B] font-bold text-sm flex items-center gap-2">
                <Crown size={18} strokeWidth={2.5} /> Premium
              </span>
            ) : (
              <>
                {pro && pro.state === "active" && (
                  <span data-testid="pro-window-active" title="Free Pro time left in this window" className="min-h-[48px] px-3 rounded-full border-2 border-primary/40 bg-primary/10 font-bold text-sm flex items-center gap-1.5">
                    <Timer size={18} strokeWidth={2.5} /> Pro {fmtLeft(pro.seconds_left)}
                  </span>
                )}
                {pro && pro.state === "locked" && (
                  <span data-testid="pro-window-locked" title="Your free Pro hour is used — it unlocks again later" className="min-h-[48px] px-3 rounded-full border-2 border-foreground/15 bg-secondary font-bold text-sm flex items-center gap-1.5 text-muted-foreground">
                    <Lock size={18} strokeWidth={2.5} /> Pro in {fmtLeft(pro.seconds_left)}
                  </span>
                )}
                <Link
                  to="/premium"
                  data-testid="upgrade-link"
                  className="min-h-[48px] px-4 rounded-full bg-[#ECC94B] font-bold text-sm flex items-center gap-2 transition-transform duration-200 ease-out hover:-translate-y-0.5"
                >
                  <Crown size={18} strokeWidth={2.5} /> Upgrade
                </Link>
              </>
            )
          )}
          {!isUserMode && user && (
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="min-h-[48px] min-w-[48px] px-3 rounded-full border-2 border-foreground/25 bg-white font-bold text-sm flex items-center gap-2 transition-colors duration-200 hover:bg-secondary"
            >
              <LogOut size={20} strokeWidth={2.5} /> <span className="hidden sm:inline">Log out</span>
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
